// types.ts
import type { Plan } from '../apis/partnerForm';

// Re-export Plan from partnerForm
export type { Plan };

export interface PartnerFormBasicInfo {
  fullName: string;
  businessName: string;
  mobileNumber: string;
  whatsappNumber?: string;
  gstNumber?: string;
  employeeCode?: string;
  district: string;
}

// Single service object (not array)
export interface PartnerFormServiceDetail {
  category: string;
  subcategories: string[]; // array of subcategory names
  serviceChargeCategory: 'lower' | 'same' | 'higher';
}

export interface PartnerFormServiceDetails {
  services: PartnerFormServiceDetail; // Single object, not array
}

export interface PartnerFormCoverageAreas {
  selectedPlan: Plan | null;
  selectedHexagons: string[];
}

// Branch Details Types
export interface BranchLocation {
  type: 'Point';
  coordinates: [number, number]; // [longitude, latitude]
  address?: string;
  landmark?: string;
  city?: string;
  district?: string;
  state?: string;
  pincode?: string;
}

export interface BranchOperatingHours {
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  open: string;
  close: string;
  isOpen: boolean;
}

export interface ContactPerson {
  name?: string;
  designation?: string;
  phone?: string;
  email?: string;
}

export interface BranchDetails {
  branchName: string;
  branchType: 'main' | 'franchise' | 'outlet' | 'service_center';
  contactPerson?: ContactPerson;
  phoneNumbers?: string[];
  emailAddresses?: string[];
  location: BranchLocation;
  operatingHours?: BranchOperatingHours[];
  nearbyHexagons?: string[];
}

export interface PartnerFormAdditionalInfo {
  additionalWorkQuantity?: number;
  incomeSharingPercentage: number;
}

export interface PartnerFormListingDetails {
  listingAmount: number;
  selectedCategory?: string;
  selectedAreas?: string[];
  totalServiceCharge?: number;
}

export interface PartnerFormPayment {
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
  amount: number;
  currency?: string;
}

export interface PaymentOrder {
  orderId: string;
  amount: number;
  currency: string;
  keyId: string;
}

export interface PartnerFormState {
  currentStep: number;
  totalSteps: number;
  basicInfo: PartnerFormBasicInfo;
  serviceDetails: PartnerFormServiceDetails;
  coverageAreas: PartnerFormCoverageAreas;
  branchDetails: BranchDetails | null;
  additionalInfo: PartnerFormAdditionalInfo;
  listingDetails: PartnerFormListingDetails;
  payment: PartnerFormPayment;
  documents: File[];
  uploadedDocumentUrls: string[];
  errors: Record<string, string | string[]>;
  isSubmitting: boolean;
  isCreatingOrder: boolean;
  isUploadingDocuments: boolean;
  paymentOrder?: PaymentOrder;
  submittedFormId?: string;
  isSubmitted: boolean;
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

// Payment API Types
export interface CreateOrderRequest {
  amount: number;
  currency: string;
  metadata?: {
    type: string;
    notes?: Record<string, string>;
  };
}

export interface CreateOrderResponse {
  success: boolean;
  data: {
    orderId: string;
    amount: number;
    currency: string;
    keyId: string;
    receipt?: string;
    status?: string;
  };
}

export interface VerifyPaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface VerifyPaymentResponse {
  success: boolean;
  message: string;
  data?: {
    orderId: string;
    paymentId: string;
    paymentStatus: string;
    amount: number | null;
    currency: string | null;
  };
}

// Partner Form Submission Types
export interface PartnerFormSubmissionData {
  basicInfo: PartnerFormBasicInfo;
  serviceDetails: {
    services: {
      category: string;
      subcategories: string[];
      serviceChargeCategory: 'lower' | 'same' | 'higher';
    };
  };
  coverageAreas: {
    selectedPlan: {
      planId?: string;
      planName: string;
      hexCount: number | null;
      pricePerYear: number;
      sellingPrice?: number;
      isAllKerala?: boolean;
      isTrial?: string;
      trialDays?: number;
      maxUsersAllowed?: number;
    } | null;
    selectedHexagons: string[];
  };
  branchDetails: BranchDetails;
  additionalInfo: PartnerFormAdditionalInfo;
  listingDetails: PartnerFormListingDetails;
  payment: {
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
    amount: number;
    currency: string;
  };
  documents?: string[];
}

// Hexagon Types
export interface HexagonData {
  h3Index: string;
  geometry: {
    type: string;
    coordinates: number[][][];
  };
  properties?: {
    center: [number, number];
  };
}

export interface NearbyHexagon {
  h3Index: string;
  latitude: number;
  longitude: number;
  distance?: number;
}

// Service Category Types
export interface ServiceCategory {
  category: string;
  subcategories: string[];
  categoryType?: 'local' | 'statewide';
}

// Payment breakdown
export interface PaymentBreakdown {
  planName: string;
  hexLimit: number;
  selectedCount: number;
  coverageAreaKm2: number;
  pricePerYear: number;
  totalAmount: number;
  originalPrice: number;
  discount: number;
  gstAmount: number;
  isTrial: boolean;
  trialDays: number;
  offer: {
    type: string;
    value: number;
    description: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  } | null;
}

// Step configuration
export interface StepConfig {
  label: string;
  icon: React.ReactNode;
  description: string;
  summary?: string;
}

// Razorpay integration
declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description?: string;
  image?: string;
  order_id: string;
  handler: (response: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }) => void;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, string>;
  theme?: {
    color?: string;
  };
  modal?: {
    ondismiss?: () => void;
  };
}

// Store action types
export interface PartnerFormActions {
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  updateBasicInfo: (data: Partial<PartnerFormBasicInfo>) => void;
  updateServiceDetails: (data: Partial<PartnerFormServiceDetails>) => void;
  setService: (service: PartnerFormServiceDetail) => void;
  updateCoverageAreas: (data: Partial<PartnerFormCoverageAreas>) => void;
  setCoveragePlan: (plan: Plan | null) => void;
  setSelectedHexagons: (hexagons: string[]) => void;
  updateBranchDetails: (data: Partial<BranchDetails>) => void;
  setBranchDetails: (branch: BranchDetails | null) => void;
  updateAdditionalInfo: (data: Partial<PartnerFormAdditionalInfo>) => void;
  updateListingDetails: (data: Partial<PartnerFormListingDetails>) => void;
  updatePayment: (data: Partial<PartnerFormPayment>) => void;
  setDocuments: (files: File[]) => void;
  setUploadedDocumentUrls: (urls: string[]) => void;
  setError: (field: string, error: string | string[]) => void;
  clearError: (field: string) => void;
  clearAllErrors: () => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setIsCreatingOrder: (isCreating: boolean) => void;
  setIsUploadingDocuments: (isUploading: boolean) => void;
  setPaymentOrder: (order: PaymentOrder | undefined) => void;
  setSubmittedFormId: (id: string) => void;
  resetForm: () => void;
}

export type PartnerFormStore = PartnerFormState & PartnerFormActions;

// Constants
export const TOTAL_STEPS = 7;
export const SERVICE_CHARGE_CATEGORIES = [
  { value: 'lower', label: 'Lower than market' },
  { value: 'same', label: 'Same as market' },
  { value: 'higher', label: 'Higher than market' },
] as const;
export const INCOME_SHARING_MIN = 1;
export const INCOME_SHARING_MAX = 25;
export const INCOME_SHARING_DEFAULT = 1;
export const HEX_AREA_KM2 = 5.16;

// Branch Types
export const BRANCH_TYPES = [
  { value: 'main', label: 'Main Branch' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'outlet', label: 'Outlet' },
  { value: 'service_center', label: 'Service Center' },
] as const;

export const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
] as const;

// Helper functions
export const isTrialPlan = (plan: Plan | null): boolean => {
  return plan?.isTrial === 'yes';
};

export const hasActiveOffer = (plan: Plan | null): boolean => {
  return !!(plan?.offer?.isActive);
};

export const getEffectivePrice = (plan: Plan | null): number => {
  if (!plan) return 0;
  if (hasActiveOffer(plan) && plan.pricing.discountedPrice) {
    return plan.pricing.discountedPrice;
  }
  return plan.pricing.finalPrice;
};

export const formatBranchAddress = (branch: BranchDetails | null): string => {
  if (!branch?.location) return '';
  const parts = [];
  if (branch.location.address) parts.push(branch.location.address);
  if (branch.location.landmark) parts.push(branch.location.landmark);
  if (branch.location.city) parts.push(branch.location.city);
  if (branch.location.district) parts.push(branch.location.district);
  if (branch.location.pincode) parts.push(branch.location.pincode);
  return parts.join(', ');
};

export const getOperatingHoursDisplay = (hours: BranchOperatingHours[] | undefined, day: string): string => {
  const dayHours = hours?.find(h => h.day === day);
  if (!dayHours || !dayHours.isOpen) return 'Closed';
  return `${dayHours.open} - ${dayHours.close}`;
};